// FULSAS-14628: Panel for TGP plans (Starter / Signature / Signature Plus)
// that require a "Does the client have a website?" Yes/No question before
// capturing the domain. When Yes, domain is required and validated. When No,
// domain is cleared and not stored.
//
// Mirrors the KeapAddonPanel pattern: isFieldVisible controls conditional
// rendering, validateFields enforces the business rules, and
// buildOrderConfiguration shapes the configuredOptions payload.
import React, {useState} from 'react'
import {getPriceForCountry} from '../../../utils/getPriceForCountry'
import {PlanDynamicField} from '../PlanDynamicField'
import {YesNoField} from '../fields/YesNoField'
import {
  getFieldsFromPlan,
  getDefaultValues,
  validatePlanFields,
} from '../planFieldsConfig'
import {
  FieldsGrid,
  AddToOrderRow,
  PanelContainer,
  EntitlementsList,
  EntitlementsTitle,
  EntitlementItem,
} from '../ProductPlanSelector.styles'
import {Button} from '../../../../../accountManagement/shared/Button'

const HAS_WEBSITE_FIELD = {
  key: 'hasWebsite',
  label: 'Does the client have a website?',
  type: 'yes-no',
  validators: ['required'],
}

function isFieldVisible(field, values) {
  if (field.key === 'domain') return values.hasWebsite === 'yes'
  return true
}

function validateFields(fields, values, {validExtensionList} = {}) {
  if (!values.hasWebsite) {
    return {hasWebsite: 'Please select Yes or No'}
  }

  const visibleFields = fields.filter(f => isFieldVisible(f, values))

  // Make domain required when hasWebsite === 'yes'
  const fieldsToValidate = visibleFields.map(f =>
    f.key === 'domain' && !f.validators?.includes('required')
      ? {...f, validators: [...(f.validators ?? []), 'required']}
      : f,
  )

  return validatePlanFields(fieldsToValidate, values, {validExtensionList})
}

function buildOrderConfiguration(values) {
  const config = {hasWebsite: values.hasWebsite}
  if (values.hasWebsite === 'yes' && values.domain) {
    config.domain = values.domain
  }
  // Copy any other field values (e.g. onboarding yes-no on Signature plans)
  Object.keys(values).forEach(key => {
    if (key !== 'hasWebsite' && key !== 'domain') {
      if (values[key] !== undefined && values[key] !== '') {
        config[key] = values[key]
      }
    }
  })
  return config
}

export function TgpWebsitePanel({
  plan,
  selectedCountry,
  prodType,
  itemInOrder,
  groupPlanCodes,
  groupId,
  validExtensionList,
  onAddProduct,
  onUpdateProduct,
  onClose,
}) {
  const allFields = getFieldsFromPlan(plan)
  const entitlements = plan?.entitlements ?? []
  const isItemInOrder = Boolean(itemInOrder)

  const [fieldValues, setFieldValues] = useState(() => ({
    hasWebsite: null,
    ...getDefaultValues(allFields),
    ...itemInOrder?.configuredOptions,
  }))
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [selectedEntitlements, setSelectedEntitlements] = useState(() => {
    if (itemInOrder) {
      const savedCodes = new Set(
        (itemInOrder.addons ?? []).map(a => a.productCode ?? a.code),
      )
      return entitlements
        .filter(e => e.isRequired || savedCodes.has(e.code))
        .map(e => ({...e, quantity: e.quantity ?? 1}))
    }
    return entitlements
      .filter(e => e.isRequired || e.code === 'ent-5-website')
      .map(e => ({...e, quantity: e.quantity ?? 1}))
  })

  const visibleFields = allFields.filter(f => isFieldVisible(f, fieldValues))

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => {
      const next = {...prev, [key]: value}
      // Changing hasWebsite to 'no' clears the domain
      if (key === 'hasWebsite' && value === 'no') {
        next.domain = ''
      }
      return next
    })
    setErrors(prev => ({...prev, [key]: null}))
  }

  const handleFieldBlur = key => {
    setTouched(prev => ({...prev, [key]: true}))
    const fieldErrors = validateFields(allFields, fieldValues, {
      validExtensionList,
    })
    setErrors(prev => ({...prev, [key]: fieldErrors[key] ?? null}))
  }

  const handleEntitlementChange = (ent, checked) => {
    setSelectedEntitlements(prev =>
      checked
        ? [...prev, {...ent, quantity: ent.quantity ?? 1}]
        : prev.filter(e => e.code !== ent.code),
    )
  }

  const handleAddToOrder = () => {
    const fieldErrors = validateFields(allFields, fieldValues, {
      validExtensionList,
    })
    const allVisible = [HAS_WEBSITE_FIELD, ...visibleFields]
    const allTouched = allVisible.reduce(
      (acc, f) => ({...acc, [f.key]: true}),
      {},
    )
    setTouched(prev => ({...prev, ...allTouched}))
    setErrors(prev => ({...prev, ...fieldErrors}))
    if (Object.keys(fieldErrors).length > 0) return

    const product = {
      productId: plan.code,
      productCode: plan.code,
      name: plan.name,
      price: getPriceForCountry(plan.currencies, selectedCountry),
      quantity: 1,
      type: prodType === 'Center' ? 'software' : 'addon',
      billingFrequency:
        plan.productBillingType === 'Annual' ? 'annual' : 'monthly',
      configuredOptions: buildOrderConfiguration(fieldValues),
      addons: selectedEntitlements,
      excludes: plan.excludes ?? [],
      autoAddCenters: plan.autoAddCenters ?? [],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      productRank: plan.productRank ?? null,
      downgrades: plan.downgrades ?? [],
      parentProduct: plan.parentProduct,
    }

    if (isItemInOrder) {
      ;(onUpdateProduct ?? (p => onAddProduct(p, groupPlanCodes, groupId)))(
        product,
      )
    } else {
      onAddProduct(product, groupPlanCodes, groupId)
    }
    onClose()
  }

  return (
    <PanelContainer>
      <FieldsGrid>
        <YesNoField
          field={HAS_WEBSITE_FIELD}
          value={fieldValues.hasWebsite}
          onChange={handleFieldChange}
          error={touched.hasWebsite ? errors.hasWebsite : null}
        />
        {visibleFields.map(field => (
          <PlanDynamicField
            key={field.key}
            field={
              field.key === 'domain'
                ? {
                    ...field,
                    validators: [
                      ...(field.validators ?? []).filter(v => v !== 'required'),
                      'required',
                    ],
                  }
                : field
            }
            value={fieldValues[field.key] ?? ''}
            values={fieldValues}
            error={touched[field.key] ? errors[field.key] : null}
            touched={touched[field.key]}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            variant="form"
            size="md"
          />
        ))}
      </FieldsGrid>
      {entitlements.length > 0 && (
        <>
          <EntitlementsTitle>Entitlements</EntitlementsTitle>
          <EntitlementsList>
            {entitlements.map(ent => (
              <EntitlementItem key={ent.code}>
                <label style={{cursor: ent.isRequired ? 'default' : 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={
                      ent.isRequired ||
                      selectedEntitlements.some(e => e.code === ent.code)
                    }
                    disabled={ent.isRequired}
                    onChange={e =>
                      handleEntitlementChange(ent, e.target.checked)
                    }
                  />{' '}
                  {ent.name}
                </label>
              </EntitlementItem>
            ))}
          </EntitlementsList>
        </>
      )}
      <AddToOrderRow>
        <Button
          type="button"
          variant="action-orange"
          size="sm"
          onClick={handleAddToOrder}
        >
          {isItemInOrder ? 'Update Order' : 'Add to Order'}
        </Button>
      </AddToOrderRow>
    </PanelContainer>
  )
}
